/**
 * Mounts an OEM web component that is delivered from another origin.
 *
 * The element is acquired over the network, not through a static import, and it owns its own
 * shadow root — so this component's whole job is to load the module, create the tag, hand over
 * the inputs that cross (the session, and the page's location when the page owns the URL), and
 * stay out of the way.
 *
 * IT SETS NO ATTRIBUTES. The element observes none: its environment, API base URL and build
 * version are compiled into the bundle behind `loaderUrl`, one build per environment. A host
 * that configured it would be a host that could misconfigure it.
 */
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box } from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import type { OemNavigateDetail, OemSession } from "./contract";
import { loadOemElement } from "./loader";

export const OEM_ELEMENT_LOADING_TESTID = "oem-element-loading";
export const OEM_ELEMENT_UNAVAILABLE_TESTID = "oem-element-unavailable";

/** Written to the element as properties; each is one whole addressable state of the page. */
export interface OemNavigation {
  /** Omitted only for `si-seo-geo`, which has no `view` and reads its screen out of `params`. */
  view?: unknown;
  params: URLSearchParams;
  hrefFor?: (view: unknown, query?: Readonly<Record<string, string>>) => string;
  /** `si-oem-compliance` only: where its brand page links to that brand's SEO sites. */
  hrefForSeoSites?: (brand: string) => string;
  /** `si-oem-compliance` only: where its brand page opens that brand's scoring check. */
  hrefForBrandScoring?: (brand: string) => string;
  /** `si-seo-geo` only: where its Run scoring link opens a scoring check of one site. */
  hrefForScoringCheck?: (brand: string, url: string) => string;
  navigateEvent: string;
  onNavigate: (detail: OemNavigateDetail) => void;
}

export interface OemElementProps {
  /** The one published URL for this environment, from `libs/core/enviroment`. */
  loaderUrl: string;
  /** The custom element the bundle registers, from `./contract`. */
  tagName: string;
  /** Omitted means the element runs anonymous. A property on the element, never an attribute. */
  session?: OemSession;
  /** The element's own `-session-ended` event name, from `./contract`. */
  sessionEndedEvent?: string;
  /** Called when the element reports that the service refused its token. */
  onSessionEnded?: () => void;
  /**
   * Makes the page the owner of the element's location. Called with the loaded module, because
   * the view grammar is the bundle's; `undefined` means the location names no screen of it.
   * Memoise it: a new function is a new location, and every write re-renders the element.
   */
  navigation?: (bundle: unknown) => OemNavigation | undefined;
  /** Rendered in the element's place while `navigation` names no screen. */
  notFound?: ReactNode;
}

type Acquisition =
  | { state: "pending" | "failed" }
  | { state: "ready"; bundle: unknown };

interface OemHostElement extends HTMLElement {
  session?: OemSession;
  view?: unknown;
  params?: URLSearchParams;
  hrefFor?: OemNavigation["hrefFor"];
  hrefForSeoSites?: OemNavigation["hrefForSeoSites"];
  hrefForBrandScoring?: OemNavigation["hrefForBrandScoring"];
  hrefForScoringCheck?: OemNavigation["hrefForScoringCheck"];
}

export const OemElement = ({
  loaderUrl,
  tagName,
  session,
  sessionEndedEvent,
  onSessionEnded,
  navigation,
  notFound
}: OemElementProps) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<OemHostElement | null>(null);
  const configuredRef = useRef<OemNavigation | undefined>(undefined);
  const [acquisition, setAcquisition] = useState<Acquisition>({
    state: "pending"
  });

  // Acquisition happens once per URL, so it is deliberately not in the mounting effect below —
  // that one re-runs whenever the session changes. `loadOemElement` is idempotent per URL, so a
  // remount after a failure retries rather than staying empty.
  useEffect(() => {
    let live = true;
    loadOemElement(loaderUrl, tagName).then(
      bundle => {
        if (live) setAcquisition({ state: "ready", bundle });
      },
      () => {
        if (live) setAcquisition({ state: "failed" });
      }
    );
    return () => {
      live = false;
    };
  }, [loaderUrl, tagName]);

  // Memoised on the parts rather than the object: the element re-renders on every property
  // write, so a fresh identity each render would re-render it for nothing.
  const token = session?.token;
  const user = session?.user;
  const hostSession = useMemo<OemSession | undefined>(
    () =>
      token === undefined || user === undefined ? undefined : { token, user },
    [token, user]
  );

  // A bundle deploys on its own team's release, so one without the grammar `navigation` reads
  // is a broken release: it shows as unavailable rather than taking the page down.
  const resolution = useMemo(() => {
    if (acquisition.state !== "ready") return { broken: false };
    try {
      return { broken: false, resolved: navigation?.(acquisition.bundle) };
    } catch {
      return { broken: true };
    }
  }, [acquisition, navigation]);
  const { broken, resolved } = resolution;
  const isNotFound =
    acquisition.state === "ready" &&
    !broken &&
    navigation !== undefined &&
    resolved === undefined;

  useEffect(() => {
    const slot = slotRef.current;
    if (slot === null) return;
    // Before the module resolves the tag is unregistered, and `createElement` returns an
    // HTMLUnknownElement that silently accepts the property below and upgrades into nothing.
    if (acquisition.state !== "ready") return;
    // An existing instance is kept hidden rather than removed, so a mistyped path costs it
    // nothing; none is created for a location that names no screen.
    if (isNotFound || broken) return;

    const element: OemHostElement =
      elementRef.current ?? (document.createElement(tagName) as OemHostElement);

    // Every setter re-renders the element, with no equality check of its own.
    if (element.session !== hostSession) element.session = hostSession;
    if (resolved !== undefined && configuredRef.current !== resolved) {
      configuredRef.current = resolved;
      // One synchronous block: the element checks the `view`/`params` pair on a microtask, and
      // `params` without `view` tears compliance and scoring down into a configuration error.
      if (resolved.view !== undefined) element.view = resolved.view;
      element.params = resolved.params;
      if (resolved.hrefFor !== undefined) element.hrefFor = resolved.hrefFor;
      if (resolved.hrefForSeoSites !== undefined)
        element.hrefForSeoSites = resolved.hrefForSeoSites;
      if (resolved.hrefForBrandScoring !== undefined)
        element.hrefForBrandScoring = resolved.hrefForBrandScoring;
      if (resolved.hrefForScoringCheck !== undefined)
        element.hrefForScoringCheck = resolved.hrefForScoringCheck;
    }

    // Configure first, append second: `connectedCallback` runs on append, so appending first
    // would mount the element before it has been told anything.
    if (elementRef.current === null) {
      elementRef.current = element;
      slot.append(element);
    }
  }, [acquisition, tagName, hostSession, resolved, isNotFound, broken]);

  // Heard on the slot rather than the element: both events are `bubbles` + `composed`, and the
  // slot exists before the element does, so no listener waits on the element being created.
  const navigateEvent = resolved?.navigateEvent;
  const onNavigate = resolved?.onNavigate;
  useEffect(() => {
    const slot = slotRef.current;
    if (
      slot === null ||
      navigateEvent === undefined ||
      onNavigate === undefined
    )
      return;
    const handleNavigate = (event: Event) =>
      onNavigate((event as CustomEvent<OemNavigateDetail>).detail);
    slot.addEventListener(navigateEvent, handleNavigate);
    return () => slot.removeEventListener(navigateEvent, handleNavigate);
  }, [acquisition, navigateEvent, onNavigate]);

  useEffect(() => {
    const slot = slotRef.current;
    if (
      slot === null ||
      sessionEndedEvent === undefined ||
      onSessionEnded === undefined
    )
      return;
    const handleSessionEnded = () => onSessionEnded();
    slot.addEventListener(sessionEndedEvent, handleSessionEnded);
    return () =>
      slot.removeEventListener(sessionEndedEvent, handleSessionEnded);
  }, [acquisition, sessionEndedEvent, onSessionEnded]);

  // Detaching runs the element's own `disconnectedCallback`, which unmounts its React root and
  // cancels its in-flight requests. Kept in its own []-dependency effect so a re-render never
  // tears the instance down — that would take its cache and the focused element with it.
  useEffect(
    () => () => {
      elementRef.current?.remove();
      elementRef.current = null;
    },
    []
  );

  if (acquisition.state === "failed" || broken) {
    return (
      <Alert
        data-testid={OEM_ELEMENT_UNAVAILABLE_TESTID}
        role="alert"
        type="error"
      >
        These screens could not be loaded. Reloading usually resolves it; if it
        does not, the release is still rolling out.
      </Alert>
    );
  }

  return (
    <Box>
      {acquisition.state === "pending" && (
        <LoadingIndicator
          actionName="Loading OEM Compliance Center"
          data-testid={OEM_ELEMENT_LOADING_TESTID}
          size="small"
          text="Loading the OEM compliance screens"
        />
      )}
      {/* A leaf: the element owns its shadow root, so React must never render children into it.
          `Box` forwards its ref to the root DOM node, so the repo's Interstate convention holds
          even for an imperatively populated slot. */}
      <Box ref={slotRef} sx={isNotFound ? { display: "none" } : undefined} />
      {isNotFound && notFound}
    </Box>
  );
};
